<template>
  <v-card outlined>
    <v-card-title class="py-2">
      <span class="subtitle-1">{{ $t('ticketSummaries') }}</span>
      <v-chip x-small class="ml-2">{{ tickets.length }}</v-chip>
    </v-card-title>
    <v-card-text v-if="tickets.length === 0">
      <span class="text--secondary">{{ $t('noTicketsReadyForPaymentOnContract') }}</span>
    </v-card-text>
    <v-card-text v-else>
      <div class="d-flex flex-wrap mb-2">
        <v-chip
        small
        class="mr-2 mb-2 interactive"
        :color="selectedSortKey === undefined ? 'primary' : undefined"
        :text-color="selectedSortKey === undefined ? 'white' : undefined"
        @click="selectedSortKey = undefined">
          {{ $t('all') }} ({{ tickets.length }})
        </v-chip>
        <v-chip
        small
        class="mr-2 mb-2 interactive"
        v-for="filter in sortFilters"
        :key="filter.key"
        :color="selectedSortKey === filter.key ? 'primary' : undefined"
        :text-color="selectedSortKey === filter.key ? 'white' : undefined"
        @click="selectedSortKey = filter.key">
          {{ filter.label }} ({{ filter.count }})
        </v-chip>
      </div>
      <v-data-table
      dense
      class="small-headers clickable-rows"
      item-key="ticketId"
      :headers="headers"
      :items="filteredTickets"
      :items-per-page="10"
      @click:row="ticket => $emit('ticket-selected', ticket)">
        <template #item.sorts="{ item }">
          <v-tooltip bottom v-for="(sort, index) in item.sorts" :key="`${item.ticketId}-${index}`">
            <template #activator="{ on }">
              <v-chip
              x-small
              v-on="on"
              class="mr-1 my-1"
              :color="sortKeyFor(sort) === selectedSortKey ? 'primary' : undefined"
              :text-color="sortKeyFor(sort) === selectedSortKey ? 'white' : undefined">
                <span class="d-inline-block text-truncate" style="max-width: 160px;">{{ labelFor(sort) }}</span>
              </v-chip>
            </template>
            <span>{{ labelFor(sort) }}</span>
          </v-tooltip>
        </template>
        <template #item.netTons="{ item }">{{ numberWithCommas(item.netTons, 3) }}</template>
        <template #item.grossVolume="{ item }">{{ mbfStringFromBf(item.grossVolume) }}</template>
        <template #item.netVolume="{ item }">{{ mbfStringFromBf(item.netVolume) }}</template>
        <template #body.append>
          <tr class="font-weight-bold">
            <td :colspan="headers.length - 3">{{ $t('total') }}</td>
            <td class="text-right">{{ numberWithCommas(totals.netTons, 3) }}</td>
            <td class="text-right">{{ mbfStringFromBf(totals.grossVolume) }}</td>
            <td class="text-right">{{ mbfStringFromBf(totals.netVolume) }}</td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { sortKeyFor, sortLabelFor } from '@/utils/SortDimensions'
import { mbfStringFromBf, numberWithCommas } from '@/utils/NumericMutations'

export default {
  name: 'ContractSortTicketSummaries',

  props: {
    tickets: {
      type: Array,
      required: true
    }
  },

  data: () => ({
    selectedSortKey: undefined
  }),

  computed: {
    headers () {
      return [
        { text: this.$t('hashSign'), value: 'ticketNumber' },
        { text: this.$t('extTicketNum1'), value: 'extTicketNumber1' },
        { text: this.$t('extTicketNum2'), value: 'extTicketNumber2' },
        { text: this.$t('product'), value: 'product' },
        { text: this.$t('sorts'), value: 'sorts', sortable: false },
        { text: this.$t('netTons'), value: 'netTons', align: 'right' },
        { text: this.$t('grossMbf'), value: 'grossVolume', align: 'right' },
        { text: this.$t('netMbf'), value: 'netVolume', align: 'right' }
      ]
    },

    sortFilters () {
      const counts = new Map()

      for (const ticket of this.tickets) {
        for (const key of new Set(ticket.sorts.map(sortKeyFor))) {
          const existing = counts.get(key)
          counts.set(key, {
            key,
            label: existing?.label ?? this.labelFor(ticket.sorts.find(s => sortKeyFor(s) === key)),
            count: (existing?.count ?? 0) + 1
          })
        }
      }

      return [...counts.values()].sort((a, b) => b.count - a.count)
    },

    filteredTickets () {
      if (this.selectedSortKey === undefined) return this.tickets
      return this.tickets.filter(ticket => ticket.sorts.some(sort => sortKeyFor(sort) === this.selectedSortKey))
    },

    totals () {
      return this.filteredTickets.reduce((acc, ticket) => ({
        netTons: acc.netTons + (ticket.netTons ?? 0),
        grossVolume: acc.grossVolume + (ticket.grossVolume ?? 0),
        netVolume: acc.netVolume + (ticket.netVolume ?? 0)
      }), { netTons: 0, grossVolume: 0, netVolume: 0 })
    }
  },

  methods: {
    sortKeyFor,
    mbfStringFromBf,
    numberWithCommas,

    labelFor (sort) {
      return sortLabelFor(sort, this.$t('unsorted'))
    }
  }
}
</script>

<style scoped>
.clickable-rows ::v-deep tbody > tr:not(.font-weight-bold) {
  cursor: pointer;
}
</style>
